import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../services/api';
import { getAllDealerAssistant } from '../services/api';
import PresetEditor from './PresetEditor';
import DealerADFConfig from './DealerADFConfig';

const SOURCE_OPTIONS = [
  { value: 'webchat', label: 'Webchat' },
  { value: 'sms', label: 'SMS' },
  { value: 'facebook', label: 'Facebook Messenger' },
  { value: 'marketplace', label: 'Facebook Marketplace' },
  { value: 'talklive', label: 'TalkLive' },
  { value: 'phenix', label: 'Phenix' },
];

const ADFConfig = () => {
  // Presets state
  const [presets, setPresets] = useState({});
  const [selectedPresetId, setSelectedPresetId] = useState(null);
  const [showPresetEditor, setShowPresetEditor] = useState(false);
  const [isNewPreset, setIsNewPreset] = useState(false);

  // Dealer configs state
  const [dealerConfigs, setDealerConfigs] = useState([]);
  const [dealers, setDealers] = useState([]);
  const [selectedDealerConfig, setSelectedDealerConfig] = useState(null);
  const [showDealerEditor, setShowDealerEditor] = useState(false);

  // General
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusMessage, setStatusMessage] = useState('');

  const showStatus = (msg) => {
    setStatusMessage(msg);
    setTimeout(() => setStatusMessage(''), 3000);
  };

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [presetsRes, configsRes, dealersRes] = await Promise.all([
        api.get('v1/adf/presets'),
        api.get('v1/adf/configs'),
        getAllDealerAssistant(),
      ]);
      setPresets(presetsRes.data || {});
      setDealerConfigs(Array.isArray(configsRes.data) ? configsRes.data : []);
      // getAllDealerAssistant may return {data: [...]} or {data: {data: [...]}}
      const rawDealers = dealersRes.data;
      setDealers(Array.isArray(rawDealers) ? rawDealers : (rawDealers?.data || []));
    } catch (err) {
      console.error('Error loading ADF data:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  // ---- Preset handlers ----

  const handleEditPreset = (presetId) => {
    setSelectedPresetId(presetId);
    setIsNewPreset(false);
    setShowPresetEditor(true);
  };

  const handleNewPreset = () => {
    setSelectedPresetId(null);
    setIsNewPreset(true);
    setShowPresetEditor(true);
  };

  const handleSavePreset = async (presetId, presetData) => {
    try {
      if (isNewPreset) {
        await api.post(`v1/adf/presets?preset_id=${encodeURIComponent(presetId)}`, presetData);
        showStatus(`Preset "${presetId}" created`);
      } else {
        await api.put(`v1/adf/presets/${encodeURIComponent(presetId)}`, presetData);
        showStatus(`Preset "${presetId}" updated`);
      }
      setShowPresetEditor(false);
      loadData();
    } catch (err) {
      alert(`Error saving preset: ${err.response?.data?.detail || err.message}`);
    }
  };

  const handleDeletePreset = async (presetId) => {
    if (!window.confirm(`Delete preset "${presetId}"?`)) return;
    try {
      await api.delete(`v1/adf/presets/${encodeURIComponent(presetId)}`);
      showStatus(`Preset "${presetId}" deleted`);
      loadData();
    } catch (err) {
      alert(`Error deleting preset: ${err.response?.data?.detail || err.message}`);
    }
  };

  // ---- Dealer config handlers ----

  const handleEditDealerConfig = (config) => {
    setSelectedDealerConfig(config);
    setShowDealerEditor(true);
  };

  const handleNewDealerConfig = () => {
    setSelectedDealerConfig(null);
    setShowDealerEditor(true);
  };

  const handleSaveDealerConfig = async (config, isNew) => {
    try {
      if (isNew) {
        await api.post('v1/adf/configs', config);
        showStatus(`Config created for "${config.assistant_id}"`);
      } else {
        await api.put(`v1/adf/configs/${encodeURIComponent(config.assistant_id)}`, config);
        showStatus(`Config updated for "${config.assistant_id}"`);
      }
      setShowDealerEditor(false);
      loadData();
    } catch (err) {
      alert(`Error saving config: ${err.response?.data?.detail || err.message}`);
    }
  };

  const handleDeleteDealerConfig = async (assistantId) => {
    if (!window.confirm(`Delete ADF config for "${assistantId}"?`)) return;
    try {
      await api.delete(`v1/adf/configs/${encodeURIComponent(assistantId)}`);
      showStatus(`Config deleted for "${assistantId}"`);
      loadData();
    } catch (err) {
      alert(`Error deleting config: ${err.response?.data?.detail || err.message}`);
    }
  };

  // ---- Build merged dealer list (all dealers + ADF config status) ----

  const normalize = (s) => (s || '').toLowerCase().replace(/[_\-]/g, ' ');

  // Index ADF configs by assistant_id for quick lookup
  const configsByAssistantId = {};
  dealerConfigs.forEach((c) => { configsByAssistantId[c.assistant_id] = c; });

  // Merge: every dealer appears, with its ADF config if it has one
  const allDealersWithConfig = dealers.map((d) => ({
    assistant_id: d.assistant_id,
    name: d.name || '',
    adfConfig: configsByAssistantId[d.assistant_id] || null,
  }));

  const filteredDealers = allDealersWithConfig.filter((d) => {
    if (!searchTerm.trim()) return true;
    const haystack = normalize(d.assistant_id) + ' ' + normalize(d.name);
    const words = searchTerm.toLowerCase().split(/\s+/).filter(Boolean);
    return words.every((w) => haystack.includes(w));
  });

  const presetEntries = Object.entries(presets);

  if (loading) return <div className="loading">Loading ADF configuration...</div>;

  return (
    <div style={{ padding: '20px' }}>
      <h2>ADF Configuration</h2>

      {statusMessage && (
        <div style={{
          background: '#d4edda', color: '#155724', padding: '10px 16px',
          borderRadius: '4px', marginBottom: '16px', border: '1px solid #c3e6cb'
        }}>
          {statusMessage}
        </div>
      )}

      <div style={{ display: 'flex', gap: '24px' }}>
        {/* Left: Presets */}
        <div style={{ width: '320px', flexShrink: 0 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
            <h3 style={{ margin: 0 }}>Presets</h3>
            <button onClick={handleNewPreset} className="btn-primary" style={{ padding: '4px 12px', fontSize: '13px' }}>
              + New
            </button>
          </div>

          {presetEntries.length === 0 ? (
            <p style={{ color: '#888', fontSize: '14px' }}>No presets yet. Create one to get started.</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              {presetEntries.map(([id, preset]) => (
                <div key={id} style={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  padding: '8px 12px', border: '1px solid #ddd', borderRadius: '4px',
                  background: '#fafafa', fontSize: '14px',
                }}>
                  <div>
                    <strong>{preset.name || id}</strong>
                    <div style={{ fontSize: '12px', color: '#888' }}>{id}</div>
                  </div>
                  <div style={{ display: 'flex', gap: '4px' }}>
                    <button onClick={() => handleEditPreset(id)} style={{ padding: '2px 8px', fontSize: '12px' }}>Edit</button>
                    <button onClick={() => handleDeletePreset(id)} style={{ padding: '2px 8px', fontSize: '12px', color: '#c00' }}>Del</button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Right: Dealer configs */}
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
            <h3 style={{ margin: 0 }}>Dealer Configs</h3>
            <button onClick={handleNewDealerConfig} className="btn-primary" style={{ padding: '4px 12px', fontSize: '13px' }}>
              + New Config
            </button>
          </div>

          <input
            type="text"
            placeholder="Search by name or assistant ID..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ width: '100%', padding: '8px', marginBottom: '12px', borderRadius: '4px', border: '1px solid #ccc' }}
          />

          {filteredDealers.length === 0 ? (
            <p style={{ color: '#888', fontSize: '14px' }}>No dealers found.</p>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #ddd', textAlign: 'left' }}>
                  <th style={{ padding: '8px' }}>Name</th>
                  <th style={{ padding: '8px' }}>Assistant ID</th>
                  <th style={{ padding: '8px' }}>ADF Status</th>
                  <th style={{ padding: '8px' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredDealers.map((dealer) => {
                  const hasConfig = !!dealer.adfConfig;
                  const productKeys = hasConfig ? Object.keys(dealer.adfConfig.products || {}) : [];
                  const sourceKeys = hasConfig ? Object.keys(dealer.adfConfig.sources || {}) : [];
                  const statusKeys = productKeys.length > 0 ? productKeys : sourceKeys;
                  return (
                    <tr key={dealer.assistant_id} style={{ borderBottom: '1px solid #eee' }}>
                      <td style={{ padding: '8px' }}>{dealer.name || '—'}</td>
                      <td style={{ padding: '8px', fontFamily: 'monospace', fontSize: '12px' }}>{dealer.assistant_id}</td>
                      <td style={{ padding: '8px' }}>
                        {hasConfig && statusKeys.length > 0 ? (
                          statusKeys.map((s) => (
                            <span key={s} style={{
                              display: 'inline-block', padding: '2px 6px', marginRight: '4px',
                              background: productKeys.includes(s) ? '#d4edda' : '#fff3cd',
                              borderRadius: '3px', fontSize: '11px',
                              color: productKeys.includes(s) ? '#155724' : '#856404',
                            }}>{s}</span>
                          ))
                        ) : hasConfig ? (
                          <span style={{ color: '#999', fontSize: '12px' }}>Configured</span>
                        ) : (
                          <span style={{ color: '#999', fontSize: '12px' }}>Not configured</span>
                        )}
                      </td>
                      <td style={{ padding: '8px' }}>
                        {hasConfig ? (
                          <>
                            <button onClick={() => handleEditDealerConfig(dealer.adfConfig)} style={{ padding: '2px 8px', fontSize: '12px', marginRight: '4px' }}>
                              Edit
                            </button>
                            <button onClick={() => handleDeleteDealerConfig(dealer.assistant_id)} style={{ padding: '2px 8px', fontSize: '12px', color: '#c00' }}>
                              Delete
                            </button>
                          </>
                        ) : (
                          <button
                            onClick={() => {
                              setSelectedDealerConfig({
                                assistant_id: dealer.assistant_id,
                                assistant_name: dealer.name,
                                sources: {},
                                products: {},
                              });
                              setShowDealerEditor(true);
                            }}
                            style={{ padding: '2px 8px', fontSize: '12px' }}
                          >
                            + Configure
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Preset Editor Modal */}
      {showPresetEditor && (
        <PresetEditor
          presetId={selectedPresetId}
          presetData={selectedPresetId ? presets[selectedPresetId] : null}
          isNew={isNewPreset}
          onSave={handleSavePreset}
          onClose={() => setShowPresetEditor(false)}
        />
      )}

      {/* Dealer Config Editor Modal */}
      {showDealerEditor && (
        <DealerADFConfig
          config={selectedDealerConfig}
          dealers={dealers}
          presets={presets}
          sourceOptions={SOURCE_OPTIONS}
          onSave={handleSaveDealerConfig}
          onClose={() => setShowDealerEditor(false)}
        />
      )}
    </div>
  );
};

export default ADFConfig;
